import { c as _c } from "react/compiler-runtime";
import { useQueryClient } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { createMemoryHistory, createRouter, RouterProvider } from '@tanstack/react-router';
import { useState } from 'react';
import { InitShimmer } from './components/init-shimmer';
import { useIdvToIdv2Sync } from './features/idv-to-idv2';
import type { IdentifyResult } from './routes/router-context';
import { routeTree } from './routes/routeTree.gen';
import { shouldWaitForComponentsSdk } from './utils/route-helpers';
import { setupRouteTracking } from './utils/route-tracking';

// AppShell renders the real navigation header in the frame this covers, so the skeleton must not
// reserve header space of its own.
const PendingShimmer = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <InitShimmer reserveHeader={false} />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
export type Idv2Props = {
  /** Initial route when embedded: sandbox-outcome (pre-identify), identify flow, onboarding flow (with token in path), auth-methods management (identify runs first, with the tenant's initialAuthToken), or handoff (passkey registration target). */
  initialPath?: '/sandbox-outcome' | '/identify' | '/onboarding' | '/auth-methods' | '/handoff';
  /** Identify session scope, sent to the backend when identify creates its session. */
  scope?: 'auth' | 'onboarding';
  /** When set, identify completion calls this then navigates to /onboarding (unified flow). */
  onIdentifyDone?: (result: IdentifyResult) => void;
  /** Called when auth-methods management flow completes. */
  onAuthMethodsDone?: () => void;
};
export const Idv2 = t0 => {
  const $ = _c(19);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    initialPath: t2,
    scope: t3,
    onIdentifyDone,
    onAuthMethodsDone
  } = t1;
  const initialPath = t2 === undefined ? "/onboarding" : t2;
  const scope = t3 === undefined ? "onboarding" : t3;
  const initialValues = useIdvToIdv2Sync();
  const queryClient = useQueryClient();
  let t4;
  if ($[2] !== initialPath || $[3] !== initialValues.componentsSdkRelay || $[4] !== initialValues.onboardingSessionToken) {
    t4 = () => {
      const token = initialValues.onboardingSessionToken;
      let path;
      if (initialPath === "/onboarding" && shouldWaitForComponentsSdk(initialValues.componentsSdkRelay)) {
        path = `/wait-for-components-sdk/${token}`;
      } else {
        if (initialPath === "/onboarding") {
          path = `/onboarding/${token}`;
        } else {
          if (initialPath === "/auth-methods") {
            path = "/identify";
          } else {
            path = initialPath;
          }
        }
      }
      const history = createMemoryHistory({
        initialEntries: [path]
      });
      const r = createRouter({
        routeTree,
        history,
        defaultPreload: "intent",
        defaultPreloadDelay: 100
      });
      setupRouteTracking(r);
      return r;
    };
    $[2] = initialPath;
    $[3] = initialValues.componentsSdkRelay;
    $[4] = initialValues.onboardingSessionToken;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const [router] = useState(t4);
  const t5 = initialPath === "/auth-methods" ? "/auth-methods" : undefined;
  let t6;
  if ($[6] !== initialValues || $[7] !== onAuthMethodsDone || $[8] !== onIdentifyDone || $[9] !== queryClient || $[10] !== scope || $[11] !== t5) {
    t6 = {
      ...initialValues,
      queryClient,
      scope,
      onIdentifyDone,
      onAuthMethodsDone,
      afterIdentify: t5
    };
    $[6] = initialValues;
    $[7] = onAuthMethodsDone;
    $[8] = onIdentifyDone;
    $[9] = queryClient;
    $[10] = scope;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== router || $[14] !== t6) {
    t7 = <RouterProvider defaultPendingMs={0} defaultPendingComponent={PendingShimmer} router={router} context={t6} />;
    $[13] = router;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = process.env.NODE_ENV === "development" && <ReactQueryDevtools />;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t7) {
    t9 = <>{t7}{t8}</>;
    $[17] = t7;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  return t9;
};